import {
    Box,
    Checkbox,
    Checkmark,
    Close,
    Delete,
    Edit,
    IconButton,
    StaticTable,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import * as React from "react";

import style from "components/rules/editableRulesTable.scss";
import { EditableRow, EditableRows } from "components/rules/useEditableRows";
import { isTextTruncated, resolveColor } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export interface RuleRowTestIds {
    editButton: string;
    deleteButton: string;
    confirmButton: string;
    cancelButton: string;
}

interface Props<T extends EditableRow & { validated: boolean }> {
    // Prefix for the names of the form elements, unique within the page.
    name: string;
    className: string;
    testId: string;
    rowTestIds: RuleRowTestIds;
    emptyStateMessage: string;
    selectAllLabel: string;
    headers: string[];
    // The rows to show, for example the current page of editableRows.rows.
    rows: T[];
    editableRows: EditableRows<T>;
    isRowComplete: (row: T) => boolean;
    // Render the cells between the selection checkbox and the action buttons, either as text or as inputs.
    renderCells: (row: T) => React.ReactNode[];
}

interface TruncationTooltipCellProps {
    children: React.ReactNode;
}

const hasTruncatedDescendant = (element: HTMLElement) =>
    Array.from(element.querySelectorAll<HTMLElement>("*")).some(isTextTruncated);

const TruncationTooltipCell = (props: TruncationTooltipCellProps) => {
    const contentRef = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);
    React.useLayoutEffect(() => {
        const element = contentRef.current;
        if (!element) {
            return;
        }
        const update = () => setIsTruncated(hasTruncatedDescendant(element));
        update();
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, [props.children]);
    return (
        <Tooltip placement="top">
            {isTruncated && (
                <TooltipContent>
                    <span className={style.tooltipText}>{props.children}</span>
                </TooltipContent>
            )}
            <TooltipTrigger className={style.tooltipTrigger}>
                <span ref={contentRef}>{props.children}</span>
            </TooltipTrigger>
        </Tooltip>
    );
};

// Table of rules that are edited inline, with a selection checkbox and edit and delete actions on every row.
export const EditableRulesTable = <T extends EditableRow & { validated: boolean }>(props: Props<T>) => {
    const theme = useTheme();
    const editableRows = props.editableRows;
    const containerReference = React.useRef<HTMLDivElement>(null);
    const previousRowCountReference = React.useRef(props.rows.length);

    // The table has a fixed height, so scroll to a newly added row to keep it visible.
    React.useEffect(() => {
        const container = containerReference.current;
        const lastRow = props.rows[props.rows.length - 1];
        const isRowAdded = props.rows.length > previousRowCountReference.current;
        previousRowCountReference.current = props.rows.length;
        if (container && isRowAdded && lastRow?.isCreated && lastRow.isEditing) {
            container.scrollTop = container.scrollHeight;
        }
    }, [props.rows.length]);

    const renderActions = (row: T) => {
        if (!row.isEditing) {
            return (
                <Box horizontalAlignment="Center" verticalAlignment="Center" gap="Xs">
                    <IconButton
                        onClick={() => {
                            (editableRows.updateRow as (id: number, field: string, value: unknown) => void)(
                                row.id,
                                "validated",
                                true
                            );
                            editableRows.editRow(row.id);
                        }}
                        testId={props.rowTestIds.editButton}
                    >
                        <Edit />
                    </IconButton>
                    <IconButton
                        deleteButton
                        onClick={() => editableRows.deleteRows([row.id])}
                        testId={props.rowTestIds.deleteButton}
                    >
                        <Delete />
                    </IconButton>
                </Box>
            );
        }
        return (
            <Box horizontalAlignment="Center" verticalAlignment="Center" gap="Xs">
                <IconButton
                    className={style.confirmIconButton}
                    onClick={() => {
                        if (!row.validated && !props.isRowComplete(row)) {
                            (editableRows.updateRow as (id: number, field: string, value: unknown) => void)(
                                row.id,
                                "validated",
                                true
                            );
                            return;
                        }
                        editableRows.confirmRow(row.id);
                    }}
                    disabled={row.validated && !props.isRowComplete(row)}
                    testId={props.rowTestIds.confirmButton}
                >
                    <Checkmark color={resolveColor("--color-white")} size={14} />
                </IconButton>
                <IconButton
                    className={style.cancelIconButton}
                    onClick={() => editableRows.cancelEdit(row.id)}
                    testId={props.rowTestIds.cancelButton}
                >
                    <Close color={theme.iconFillColor} size={14} />
                </IconButton>
            </Box>
        );
    };

    const renderContentCells = (row: T) => {
        const cells = props.renderCells(row);
        if (row.isEditing) {
            return cells;
        }
        return cells.map((cell, index) => <TruncationTooltipCell key={index}>{cell}</TruncationTooltipCell>);
    };

    const renderRow = (row: T) => [
        <Checkbox
            key="select"
            name={`${props.name}-select-${row.id}`}
            checked={editableRows.selectedIds.includes(row.id)}
            hideLabelSection={true}
            onChange={(event) => editableRows.toggleSelected(row.id, event.target.checked)}
            testIds={{ input: testIds.common.primaryView.table.selectRowCheckbox }}
        />,
        ...renderContentCells(row),
        renderActions(row),
    ];

    return (
        <div ref={containerReference} className={props.className}>
            <StaticTable
                testId={props.testId}
                emptyStateMessage={props.emptyStateMessage}
                headers={[
                    {
                        value: (
                            <Checkbox
                                name={`${props.name}-select-all`}
                                checked={editableRows.areAllSelected(props.rows)}
                                indeterminate={
                                    editableRows.selectedIds.length > 0 && !editableRows.areAllSelected(props.rows)
                                }
                                hideLabelSection={true}
                                aria-label={props.selectAllLabel}
                                onChange={(event) => editableRows.toggleAll(props.rows, event.target.checked)}
                                testIds={{ input: testIds.common.primaryView.table.selectAllCheckbox }}
                            />
                        ),
                    },
                    ...props.headers.map((header) => ({ value: header })),
                    { value: "" },
                ]}
                styledCells={props.rows.map((row) =>
                    renderRow(row).map((cell, index) => (
                        <td key={`${row.id}-${index}`} className={row.isEditing ? style.editingCell : undefined}>
                            {cell}
                        </td>
                    ))
                )}
            />
        </div>
    );
};
